import { createElement, ReactNode, RefObject, useCallback, useId, useState } from 'react';
import { Collapsible as CollapsibleComponent } from '@cbhq/cds-web/collapsible/Collapsible';
import { useA11yControlledVisibility } from '@cbhq/cds-web/hooks/useA11yControlledVisibility';
import { Icon } from '@cbhq/cds-web/icons/Icon';
import { VStack } from '@cbhq/cds-web/layout';
import { Text } from '@cbhq/cds-web/typography';

import styles from './Collapsible.module.css';

type Props = {
  triggerWrapper?: 'div' | 'p' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
  triggerLabel: ReactNode;
  triggerRef?: RefObject<HTMLButtonElement>;
  children: ReactNode;
  defaultCollapsed?: boolean;
};

function Collapsible({
  triggerWrapper = 'h2',
  triggerLabel,
  triggerRef,
  children,
  defaultCollapsed = false,
}: Props) {
  const id = useId();
  const [collapsed, setCollapsed] = useState(defaultCollapsed);

  const handleClick = useCallback(() => {
    setCollapsed(!collapsed);
  }, [collapsed]);

  // Web only a11y configuration
  const { triggerAccessibilityProps, controlledElementAccessibilityProps } =
    useA11yControlledVisibility(!collapsed, {
      accessibilityLabel: 'cds-collapsible',
      hasPopupType: 'true',
    });

  const TriggerButton = (
    <button
      key={id}
      id={id}
      ref={triggerRef}
      className={styles.trigger}
      onClick={handleClick}
      type="button"
      {...triggerAccessibilityProps}
    >
      <Text as="span" font="headline">
        {triggerLabel}
      </Text>
      <Icon
        // @ts-expect-error - Icon as="span" throws a ts error but it works
        as="span"
        className={styles.icon}
        name="caret"
        aria-hidden="true"
        role="img"
        size="s"
        color="fg"
      />
    </button>
  );

  return (
    <VStack gap={0}>
      {createElement(triggerWrapper, {}, TriggerButton)}
      <div className={styles.collapsible}>
        <CollapsibleComponent collapsed={collapsed} {...controlledElementAccessibilityProps}>
          {children}
        </CollapsibleComponent>
      </div>
    </VStack>
  );
}

export { Collapsible };
